import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  XMarkIcon, 
  ChevronRightIcon, 
  MagnifyingGlassIcon 
} from '@heroicons/react/24/outline';
import TimchehLoader from './TimchehLoader';

interface CitiesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedCities: string[]) => void;
  initialSelectedCities?: string[];
}

const CitiesModal: React.FC<CitiesModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  initialSelectedCities = []
}) => {
  // Modal state
  const [selectedProvinces, setSelectedProvinces] = useState<string[]>([]);
  const [tempSelectedCities, setTempSelectedCities] = useState<string[]>([]);
  const [locationSearch, setLocationSearch] = useState('');

  // Initialize temp selected cities when modal opens
  useEffect(() => {
    if (isOpen) {
      setTempSelectedCities([...initialSelectedCities]);
      setSelectedProvinces([]);
      setLocationSearch('');
    }
  }, [isOpen, initialSelectedCities]);

  // Fetch provinces for location modal
  const { data: provinces, isLoading: isProvincesLoading } = useQuery({
    queryKey: ['provinces'],
    queryFn: async () => {
      const response = await fetch('/api/location/provinces');
      if (!response.ok) throw new Error('Failed to fetch provinces');
      return response.json();
    },
    enabled: isOpen
  });

  // Fetch cities based on selected provinces
  const { data: cities, isLoading: isCitiesLoading } = useQuery({
    queryKey: ['cities', selectedProvinces],
    queryFn: async () => {
      if (selectedProvinces.length === 0) return [];
      
      // Fetch cities for all selected provinces
      const allCities = [];
      for (const provinceId of selectedProvinces) {
        const response = await fetch(`/api/location/provinces/${provinceId}/cities`);
        if (response.ok) {
          const provinceCities = await response.json();
          allCities.push(...provinceCities);
        }
      }
      return allCities;
    },
    enabled: isOpen && selectedProvinces.length > 0
  });

  // Handler functions
  const handleProvinceToggle = (provinceId: string) => {
    setSelectedProvinces(prev => 
      prev.includes(provinceId) 
        ? prev.filter(p => p !== provinceId)
        : [...prev, provinceId]
    );
  };

  const handleCityToggle = (cityName: string) => {
    setTempSelectedCities(prev => 
      prev.includes(cityName) 
        ? prev.filter(c => c !== cityName)
        : [...prev, cityName]
    );
  };

  const removeTempCity = (cityName: string) => {
    setTempSelectedCities(prev => prev.filter(c => c !== cityName));
  };

  const handleConfirm = () => {
    onConfirm(tempSelectedCities);
    onClose();
  };

  const handleClose = () => {
    setSelectedProvinces([]);
    setTempSelectedCities([]);
    setLocationSearch('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">انتخاب موقعیت</h2>
          <button 
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Selected Cities Display */}
        {tempSelectedCities.length > 0 && (
          <div className="p-4 border-b bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">شهرهای انتخاب شده</span>
              <button 
                onClick={() => setTempSelectedCities([])}
                className="text-xs text-red-600 hover:text-red-700"
              >
                پاک کردن همه
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {tempSelectedCities.map(city => (
                <span key={city} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                  {city}
                  <button 
                    onClick={() => removeTempCity(city)}
                    className="mr-1 hover:text-red-600"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Modal Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="p-4">
            <p className="text-sm text-gray-600 mb-4">لطفاً حداقل یک موقعیت انتخاب کنید.</p>
            
            {/* Search Input */}
            <div className="relative mb-4">
              <input
                type="text" 
                placeholder="جستجو در موقعیت‌ها"
                value={locationSearch}
                onChange={e => setLocationSearch(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border border-gray-200 rounded-lg text-sm"
                dir="rtl"
              />
              <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>

          {/* Provinces and Cities List */}
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            {!selectedProvinces.length ? (
              // Show provinces when none selected
              <div className="space-y-3">
                {isProvincesLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <TimchehLoader size="small" message="در حال بارگذاری استان‌ها..." />
                  </div>
                ) : (
                  provinces
                    ?.filter((province: any) =>
                      province.name.toLowerCase().includes(locationSearch.toLowerCase())
                    )
                    .map((province: any) => (
                      <div key={province.id} className="flex items-center justify-between py-2 border-b border-gray-100">
                        <div className="flex items-center flex-1">
                          <input
                            type="checkbox"
                            checked={selectedProvinces.includes(province.id)}
                            onChange={() => handleProvinceToggle(province.id)}
                            className="ml-3 rounded border-gray-300 text-red-600 focus:ring-red-500"
                          />
                          <span className="text-sm">{province.name}</span>
                        </div>
                        <span className="text-xs text-gray-500">{province.code}</span>
                      </div>
                    ))
                )}
              </div>
            ) : (
              // Show cities when provinces selected
              <div className="space-y-3">
                {/* Back to provinces button */}
                <button
                  className="mb-4 text-xs text-red-600 hover:text-red-800 font-medium flex items-center"
                  onClick={() => { setSelectedProvinces([]); setLocationSearch(''); }}
                >
                  <ChevronRightIcon className="h-4 w-4 ml-1 rotate-180" /> بازگشت به استان‌ها
                </button>
                {isCitiesLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <TimchehLoader size="small" message="در حال بارگذاری شهرها..." />
                  </div>
                ) : (
                  cities
                    ?.filter((city: any) =>
                      city.name.toLowerCase().includes(locationSearch.toLowerCase())
                    )
                    .map((city: any) => (
                      <div key={city.id} className="flex items-center py-2 border-b border-gray-100">
                        <input
                          type="checkbox"
                          checked={tempSelectedCities.includes(city.name)}
                          onChange={() => handleCityToggle(city.name)}
                          className="ml-3 rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                        <span className="text-sm">{city.name}</span>
                      </div>
                    ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex gap-3 p-4 border-t">
          <button
            onClick={handleConfirm}
            className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-red-700"
          >
            تایید
          </button>
          <button
            onClick={handleClose}
            className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-300"
          >
            لغو
          </button>
        </div>
      </div>
    </div>
  );
};

export default CitiesModal; 